"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
/* eslint-disable react/jsx-fragments */
const react_1 = __importDefault(require("react"));
const moment_1 = __importDefault(require("moment"));
const _ui_1 = require("@ui");
const globals_1 = require("@app/const/globals");
const util_1 = require("@app/util");
const store_1 = require("@app/store");
const set_calendar_filters_1 = __importDefault(require("@app/ac/set-calendar-filters"));
const ac_1 = require("@app/ac");
require("./index.scss");
// 'FilterObject.data' is typed as a plain 'object', so the positionId inside it needs unwrapping
const getFilterPositionId = (fil) => fil.data?.positionId;
const Filters = ({ setFiltersActive }) => {
    const dispatch = (0, store_1.useRootDispatch)();
    const calendar = (0, store_1.useRootSelector)((state) => state.calendar);
    const calendarFilters = (0, store_1.useRootSelector)((state) => state.calendarFilters);
    const employees = (0, store_1.useRootSelector)((state) => state.employees);
    const positions = (0, store_1.useRootSelector)((state) => state.positions);
    const userId = (0, store_1.useRootSelector)((state) => state.me?.id);
    const workspace = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(state.workspaceId)
        ? state.workspaces.find((ws) => ws.id === state.workspaceId)
        : undefined);
    const positionsForFilters = positions.filter((pos) => !pos.archived);
    const workspaceLocalities = (workspace?.localities ?? []).filter(util_1.isValue);
    // if this workspace has enforcedLocalities, we will display locality filter as a *singleSelect* dropdown. they must choose one.
    const myLocalities = workspaceLocalities.filter((l) => l.assigns?.some((ass) => ass.userId === userId));
    const localitiesEnforcedOnWS = Boolean(workspace?.enforceLocalities);
    const enforcedLocality = util_1.miscUtil.getEnforcedLocality(workspace);
    // people with positions
    const optsPeopleWithPos = positionsForFilters.map((p) => ({
        value: p.id,
        label: react_1.default.createElement(_ui_1.Position, { pos: p }),
        filterObject: { peopleWithPositionId: p.id },
        invertFilterValue: true // if this is true, the filter logic is inverted - when the option is checked, it is DISPLAYED in calendar, and when it's unchecked, it is FILTERED OUT
    }));
    const isCalFilterRelevantToPeopleWithPos = (f) => {
        return (0, util_1.isValue)(f.peopleWithPositionId) && f.peopleWithPositionId !== globals_1.NO_POSITION;
    };
    // shifts with positions
    const optsShiftsWithPos = positionsForFilters.map((p) => ({
        value: p.id,
        label: react_1.default.createElement(_ui_1.Position, { pos: p }),
        filterObject: { data: { positionId: p.id } },
        invertFilterValue: true // if this is true, the filter logic is inverted - when the option is checked, it is DISPLAYED in calendar, and when it's unchecked, it is FILTERED OUT
    }));
    const isCalFilterRelevantToShiftsWithPos = (fil) => {
        return (0, util_1.isValue)(getFilterPositionId(fil));
    };
    // localitites
    const optsLocalitiesHidden = [];
    const optsLocalities = workspaceLocalities
        .map((loc) => ({
        value: loc.id,
        label: react_1.default.createElement(_ui_1.Locality, { ...loc }),
        filterObject: { localityId: loc.id },
        invertFilterValue: true // if this is true, the filter logic is inverted - when the option is checked, it is DISPLAYED in calendar, and when it's unchecked, it is FILTERED OUT
    }))
        .filter((loc) => {
        // if this WS has enforcedLocalities, we filter out some locality options
        if (workspace?.enforceLocalities) {
            if (myLocalities.length === 0) {
                // if we have 0 localities, we treat it as if we had all of them and leave them all in
                return true;
            }
            else {
                // if we have more than 0 localities, we leave the ones that we have
                const ret = myLocalities.some((ml) => ml.id === loc.value);
                if (!ret)
                    optsLocalitiesHidden.push(Object.assign({}, loc));
                return ret;
            }
        }
        return true;
    })
        .concat(workspace?.enforceLocalities
        // if this ws has enforcedLocalities, we add the NO_LOCALITY option
        ? [{
                value: globals_1.NO_LOCALITY,
                label: (0, util_1.t)('GLOBAL_NO_LOCALITY'),
                filterObject: { localityId: globals_1.NO_LOCALITY }, // not used, since WS has enforcedLocalities
                invertFilterValue: true // not used, since WS has enforcedLocalities
            }]
        : []);
    const isCalFilterRelevantToLocalities = (fil) => {
        return (0, util_1.isValue)(fil.localityId) && fil.localityId !== globals_1.NO_LOCALITY;
    };
    // employees
    const optsEmployees = util_1.sortUtil
        .sortEmployees(Object.values(employees).filter((ee) => !ee.external))
        .map((ee) => ({
        value: ee.id ?? undefined,
        label: ee.name,
        filterObject: { userId: ee.id ?? undefined },
        invertFilterValue: true // if this is true, the filter logic is inverted - when the option is checked, it is DISPLAYED in calendar, and when it's unchecked, it is FILTERED OUT
    }));
    const isCalFilterRelevantToEmployees = (fil) => {
        return (0, util_1.isValue)(fil.userId);
    };
    // special HIDE filters
    const optsSpecialHideFilters = [
        {
            value: 'absencesHidden',
            label: (0, util_1.t)('CAL_FILTERS_ABSENCES'),
            filterObject: { type: 'timeOff' }
        },
        {
            value: globals_1.NO_POSITION,
            label: (0, util_1.t)('CAL_FILTERS_POSITIONS_HIDE_NO_POS'),
            filterObject: { peopleWithPositionId: globals_1.NO_POSITION }
        },
        {
            value: globals_1.NO_LOCALITY,
            label: (0, util_1.t)('CAL_FILTERS_LOCALITIES_HIDE_NO_LOC'),
            filterObject: { localityId: globals_1.NO_LOCALITY }
        },
        {
            value: 'withoutContractHidden',
            label: (0, util_1.t)('CAL_FILTERS_WITHOUT_CONT'),
            filterObject: { labelData: { userHasContractsInCalendarPeriod: false } }
        },
        {
            value: 'withoutShiftsHidden',
            label: (0, util_1.t)('CAL_FILTERS_WITHOUT_SHIFTS'),
            filterObject: { labelData: { userShiftsCount: 0 } }
        },
        {
            value: 'adultsHidden',
            label: (0, util_1.t)('CAL_FILTERS_ADULTS'),
            filterObject: { labelData: { user: { adult: true } } }
        },
        {
            value: 'underageHidden',
            label: (0, util_1.t)('CAL_FILTERS_UNDERAGE'),
            filterObject: { labelData: { user: { adult: false } } }
        },
        {
            value: 'rowsWithoutEventsHidden',
            label: (0, util_1.t)('CAL_FILTERS_POSITIONS_HIDE_EMPS_WITHOUT'),
            filterObject: { labelData: { userEventsCount: 0 } }
        },
        {
            value: 'externalHidden',
            label: (0, util_1.t)('EXTERNAL_EMPS_HIDE'),
            filterObject: { labelData: { user: { external: true } } }
        }
    ];
    // every option above matches a calendar filter by deep equality of its 'filterObject'
    const matchesSpecialHideFilter = (fil, opt) => util_1.miscUtil.safeStringify(fil) === util_1.miscUtil.safeStringify(opt.filterObject);
    const isCalFilterRelevantToHideFilters = (fil) => {
        return optsSpecialHideFilters.some((opt) => matchesSpecialHideFilter(fil, opt));
    };
    const showFilterDate = () => {
        const view = calendar?.view;
        const date = calendar?.date;
        if (view === 'month')
            return (0, util_1.t)('MONTH_' + (0, moment_1.default)(date).format('M')) + (0, moment_1.default)(date).format(' YYYY');
        if (view === 'day')
            return (0, moment_1.default)(date).format('D. MMMM');
        if (view === 'week')
            return `${(0, moment_1.default)(date).startOf('week').format('D.')} - ${(0, moment_1.default)(date).endOf('week').format('D. MMMM')}`;
    };
    // shared by both variants of the localities Dropdown below
    const localitiesDropdownProps = {
        label: (0, util_1.t)('CAL_FILTERS_LOCALITIES'),
        searchable: false,
        sortedOptions: false,
        size: _ui_1.Dropdown.SIZES.LARGE,
        type: _ui_1.Dropdown.TYPES.VARIABLE,
        style: _ui_1.Dropdown.STYLES.OUTLINED,
        extraDropdownContentPosition: _ui_1.Dropdown.EXTRA_POSITION.TOP,
        options: optsLocalities,
        placeholder: (0, util_1.t)('SELECT')
    };
    return (react_1.default.createElement(react_1.default.Fragment, null,
        react_1.default.createElement("div", { className: 'ds-display-filters-title' },
            react_1.default.createElement(_ui_1.Flex, null,
                react_1.default.createElement(_ui_1.Icon, { ico: 'filters' }),
                react_1.default.createElement(_ui_1.Spacing, { type: _ui_1.Spacing.TYPES.HORIZONTAL, size: _ui_1.Spacing.SIZES.SIZE_6 }),
                react_1.default.createElement(_ui_1.Flex, { direction: _ui_1.Flex.DIRECTION.COLUMN },
                    react_1.default.createElement(_ui_1.Text, { weight: _ui_1.Text.WEIGHTS.BOLD, type: _ui_1.Text.TYPES.BODY_LARGE, text: (0, util_1.t)('CAL_FILTERS_TITLE'), nowrap: true }),
                    react_1.default.createElement(_ui_1.Text, { weight: _ui_1.Text.WEIGHTS.BOLD, type: _ui_1.Text.TYPES.BODY_LARGE, nowrap: true }, showFilterDate())))),
        react_1.default.createElement("div", { className: 'ds-display-filter-filters' },
            react_1.default.createElement(_ui_1.Spacing, { type: _ui_1.Spacing.TYPES.HORIZONTAL, size: _ui_1.Spacing.SIZES.SIZE_28 }),
            positionsForFilters.length ? (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(_ui_1.Dropdown, { label: (0, util_1.t)('CAL_FILTERS_POSITIONS'), searchable: true, sortedOptions: false, size: _ui_1.Dropdown.SIZES.LARGE, type: _ui_1.Dropdown.TYPES.VARIABLE, style: _ui_1.Dropdown.STYLES.OUTLINED, extraDropdownContentPosition: _ui_1.Dropdown.EXTRA_POSITION.TOP, options: optsPeopleWithPos, value: optsPeopleWithPos.filter(opt => calendarFilters.some(fil => fil.peopleWithPositionId === opt.value) !== Boolean(opt.invertFilterValue)), placeholder: (0, util_1.t)('SELECT'), onChange: (valsPar) => {
                        const vals = valsPar.filter(v => !v.invertFilterValue).concat(optsPeopleWithPos.filter(opt => opt.invertFilterValue && !valsPar.some(vp => vp.value === opt.value))); // invert the values of options with 'invertFilterValue: true'
                        void dispatch((0, set_calendar_filters_1.default)(calendarFilters.filter(f => {
                            if (!isCalFilterRelevantToPeopleWithPos(f))
                                return true; // irrelevant calendar filters are kept
                            return vals.some(v => f.peopleWithPositionId === v.value); // filtering out the relevant 'calendarFilters' that are not in 'vals'
                        }).concat(vals.filter(v => !calendarFilters.some(f => f.peopleWithPositionId === v.value)).map(v => v.filterObject) // adding new 'calendarFilters' that are in 'vals'
                        )));
                    } }))) : null,
            positionsForFilters.length ? (react_1.default.createElement(react_1.default.Fragment, null,
                react_1.default.createElement(_ui_1.Dropdown, { label: (0, util_1.t)('CAL_FILTERS_POSITIONS_SHIFTS'), searchable: true, sortedOptions: false, size: _ui_1.Dropdown.SIZES.LARGE, type: _ui_1.Dropdown.TYPES.VARIABLE, style: _ui_1.Dropdown.STYLES.OUTLINED, extraDropdownContentPosition: _ui_1.Dropdown.EXTRA_POSITION.TOP, options: optsShiftsWithPos, value: optsShiftsWithPos.filter(opt => calendarFilters.some(fil => getFilterPositionId(fil) === opt.value) !== Boolean(opt.invertFilterValue)), placeholder: (0, util_1.t)('SELECT'), onChange: (valsPar) => {
                        const vals = valsPar.filter(v => !v.invertFilterValue).concat(optsShiftsWithPos.filter(opt => opt.invertFilterValue && !valsPar.some(vp => vp.value === opt.value))); // invert the values of options with 'invertFilterValue: true'
                        void dispatch((0, set_calendar_filters_1.default)(calendarFilters.filter(fil => {
                            if (!isCalFilterRelevantToShiftsWithPos(fil))
                                return true; // irrelevant calendar filters are kept
                            return vals.some(v => // filtering out the relevant 'calendarFilters' that are not in 'vals'
                             getFilterPositionId(fil) === v.value);
                        }).concat(vals.filter(v => !calendarFilters.some(fil => getFilterPositionId(fil) === v.value)).map(v => v.filterObject) // adding new 'calendarFilters' that are in 'vals'
                        )));
                    } }))) : null,
            workspaceLocalities.length ? (localitiesEnforcedOnWS ? (
            // this WS enforces localities, so exactly one must be picked
            react_1.default.createElement(_ui_1.Dropdown, { ...localitiesDropdownProps, singleSelect: true, value: (0, util_1.isValue)(enforcedLocality)
                    ? optsLocalities.find(opt => opt.value === enforcedLocality)
                    : undefined, onChange: async (vp) => {
                    await dispatch((0, ac_1.setEnforcedLocality)(vp.value));
                    dispatch((0, ac_1.workspaceSwitch)(workspace));
                } })) : (react_1.default.createElement(_ui_1.Dropdown, { ...localitiesDropdownProps, value: optsLocalities.filter(opt => calendarFilters.some(fil => fil.localityId === opt.value) !== Boolean(opt.invertFilterValue)), onChange: (valsPar) => {
                    const vals = valsPar.filter(v => !v.invertFilterValue).concat(optsLocalities.filter(opt => opt.invertFilterValue && !valsPar.some(vp => vp.value === opt.value))); // invert the values of options with 'invertFilterValue: true'
                    void dispatch((0, set_calendar_filters_1.default)(calendarFilters.filter(f => {
                        if (!isCalFilterRelevantToLocalities(f))
                            return true; // irrelevant calendar filters are kept
                        return vals.some(v => f.localityId === v.value); // filtering out the relevant 'calendarFilters' that are not in 'vals'
                    }).concat(vals.filter(v => !calendarFilters.some(f => f.localityId === v.value)).map(v => v.filterObject) // adding new 'calendarFilters' that are in 'vals'
                    ).concat(optsLocalitiesHidden.map(v => v.filterObject) // and adding whatever WS localities that were just hidden from the Dropdown, because we don't have them assigned
                    )));
                } }))) : null,
            react_1.default.createElement(_ui_1.Dropdown, { label: (0, util_1.t)('EMPLOYEES'), searchable: true, sortedOptions: false, size: _ui_1.Dropdown.SIZES.LARGE, type: _ui_1.Dropdown.TYPES.VARIABLE, style: _ui_1.Dropdown.STYLES.OUTLINED, extraDropdownContentPosition: _ui_1.Dropdown.EXTRA_POSITION.TOP, options: optsEmployees, value: optsEmployees.filter(opt => calendarFilters.some(fil => (fil.userId === opt.value)) !== Boolean(opt.invertFilterValue)), placeholder: (0, util_1.t)('SELECT'), onChange: (valsPar) => {
                    const vals = valsPar.filter(v => !v.invertFilterValue).concat(optsEmployees.filter(opt => opt.invertFilterValue && !valsPar.some(vp => vp.value === opt.value))); // invert the values of options with 'invertFilterValue: true'
                    void dispatch((0, set_calendar_filters_1.default)(calendarFilters.filter(fil => {
                        if (!isCalFilterRelevantToEmployees(fil))
                            return true;
                        return vals.some(v => // filtering out the relevant 'calendarFilters' that are not in 'vals'
                         (v.value === fil.userId));
                    }).concat(vals.filter(v => !calendarFilters.some(fil => // adding new 'calendarFilters' that are in 'vals'
                     (fil.userId === v.value))).map(v => v.filterObject))));
                } }),
            react_1.default.createElement(_ui_1.Dropdown, { label: (0, util_1.t)('CAL_FILTERS_HIDE'), searchable: true, sortedOptions: false, size: _ui_1.Dropdown.SIZES.LARGE, type: _ui_1.Dropdown.TYPES.VARIABLE, style: _ui_1.Dropdown.STYLES.OUTLINED, extraDropdownContentPosition: _ui_1.Dropdown.EXTRA_POSITION.TOP, options: optsSpecialHideFilters, value: optsSpecialHideFilters.filter(opt => calendarFilters.some(fil => matchesSpecialHideFilter(fil, opt)) !== Boolean(opt.invertFilterValue)), placeholder: (0, util_1.t)('SELECT'), onChange: (valsPar) => {
                    const vals = valsPar.filter(v => !v.invertFilterValue).concat(optsSpecialHideFilters.filter(opt => opt.invertFilterValue && !valsPar.some(vp => vp.value === opt.value))); // invert the values of options with 'invertFilterValue: true'
                    void dispatch((0, set_calendar_filters_1.default)(calendarFilters.filter(fil => {
                        if (!isCalFilterRelevantToHideFilters(fil))
                            return true;
                        return vals.some(v => matchesSpecialHideFilter(fil, v)); // filtering out the relevant 'calendarFilters' that are not in 'vals'
                    }).concat(vals.filter(v => !calendarFilters.some(fil => matchesSpecialHideFilter(fil, v))).map(v => v.filterObject) // adding new 'calendarFilters' that are in 'vals'
                    )));
                } }),
            calendarFilters.length
                ? (react_1.default.createElement(react_1.default.Fragment, null,
                    react_1.default.createElement("div", { className: 'ds-link', onClick: () => {
                            void dispatch((0, set_calendar_filters_1.default)([]));
                        } },
                        react_1.default.createElement(_ui_1.Text, { type: _ui_1.Text.TYPES.BODY_LARGE },
                            (0, util_1.t)('CAL_FILTERS_DISABLE_ALL'),
                            " (",
                            calendarFilters.length,
                            ")")),
                    react_1.default.createElement(_ui_1.Spacing, { type: _ui_1.Spacing.TYPES.HORIZONTAL, size: _ui_1.Spacing.SIZES.SIZE_28 }),
                    react_1.default.createElement(_ui_1.Button, { label: (0, util_1.t)('CLOSE'), onClick: () => setFiltersActive(false), ico: _ui_1.Icon.ICONS.close1 })))
                : (react_1.default.createElement(_ui_1.Flex, { justify: _ui_1.Flex.POSITION.END, grow: 1 },
                    react_1.default.createElement(_ui_1.Button, { label: (0, util_1.t)('CLOSE'), onClick: () => setFiltersActive(false), ico: _ui_1.Icon.ICONS.close1 }))))));
};
exports.default = Filters;
